import { createContext, useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { trackLanguageSwitch } from "./utils/analytics";

const LanguageContext = createContext();

const LANG_STORAGE_KEY = "kmc_lang_pref";

function getInitialLang() {
  try {
    const saved = localStorage.getItem(LANG_STORAGE_KEY);
    if (saved === "EN" || saved === "TH") return saved;
  } catch {
    // localStorage unavailable (e.g. privacy mode) — fall back to default
  }
  return "EN";
}

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation();
  const [lang, setLang] = useState(getInitialLang);

  // Keep i18next and <html lang="..."> in sync with the app's language state.
  // <html lang> is required for the html[lang="th"] font-scale rule in
  // styles.css to apply (rem units are relative to <html>, not to any
  // wrapper element), and it's also correct for accessibility/SEO.
  useEffect(() => {
    i18n.changeLanguage(lang.toLowerCase());
    document.documentElement.lang = lang.toLowerCase();
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      // ignore write failures (privacy mode / storage full)
    }
  }, [lang, i18n]);

  const toggle = () =>
    setLang((prev) => {
      const next = prev === "EN" ? "TH" : "EN";
      trackLanguageSwitch(next);
      return next;
    });

  return (
    <LanguageContext.Provider value={{ lang, toggle }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}
